import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  createOverlayTemplate,
  createOverlayTemplateFolder,
  deleteOverlayTemplate,
  deleteOverlayTemplateFolder,
  getOverlayTemplate,
  getOverlayTemplateScopeKey,
  listOverlayTemplateFolders,
  listOverlayTemplates,
  OverlayTemplateApiScope,
  OverlayTemplateDetailDto,
  OverlayTemplateFolderDto,
  OverlayTemplateListParams,
  OverlayTemplateSummaryDto,
  OverlayTemplateWritePayload,
  updateOverlayTemplate,
  updateOverlayTemplateFolder,
  uploadOverlayTemplatePreview as uploadOverlayTemplatePreviewFile,
  WritableOverlayTemplateApiScope
} from '@/api/overlayTemplates';
import { createDefaultTemplateForAspectRatio } from '@/lomavisSharedUi/templates/editors/LomavisTemplateEditor/templateDefinition';

export interface OverlayTemplateRequestError {
  message: string;
  fieldErrors: Record<string, unknown>;
}

export interface OverlayTemplatesState {
  templates: OverlayTemplateSummaryDto[];
  folders: OverlayTemplateFolderDto[];
  count: number;
  next: string | null;
  previous: string | null;
  activeTemplate: OverlayTemplateDetailDto | null;
  loadingLibrary: boolean;
  libraryRequestId: string | null;
  loadedLibraryScopeKey: string | null;
  loadingTemplate: boolean;
  savingTemplate: boolean;
  movingTemplateUuid: string | null;
  deletingTemplateUuid: string | null;
  mutatingFolder: boolean;
  uploadingPreview: boolean;
  error: string | null;
  fieldErrors: Record<string, unknown>;
}

const initialState: OverlayTemplatesState = {
  templates: [],
  folders: [],
  count: 0,
  next: null,
  previous: null,
  activeTemplate: null,
  loadingLibrary: false,
  libraryRequestId: null,
  loadedLibraryScopeKey: null,
  loadingTemplate: false,
  savingTemplate: false,
  movingTemplateUuid: null,
  deletingTemplateUuid: null,
  mutatingFolder: false,
  uploadingPreview: false,
  error: null,
  fieldErrors: {}
};

const toRequestError = (
  error: any,
  fallbackMessage: string
): OverlayTemplateRequestError => {
  const responseData = error?.response?.data;
  const message =
    typeof responseData?.detail === 'string'
      ? responseData.detail
      : fallbackMessage;
  const fieldErrors =
    responseData && typeof responseData === 'object' ? responseData : {};
  return { message, fieldErrors };
};

type OverlayTemplateThunkConfig = {
  rejectValue: OverlayTemplateRequestError;
};

export const fetchOverlayTemplateLibrary = createAsyncThunk<
  {
    templates: Awaited<ReturnType<typeof listOverlayTemplates>>;
    folders: Awaited<ReturnType<typeof listOverlayTemplateFolders>>;
  },
  OverlayTemplateListParams,
  OverlayTemplateThunkConfig
>('overlayTemplates/fetchLibrary', async (params, { rejectWithValue }) => {
  try {
    const [templates, folders] = await Promise.all([
      listOverlayTemplates(params),
      listOverlayTemplateFolders(params.scope)
    ]);
    return { templates, folders };
  } catch (error: any) {
    return rejectWithValue(
      toRequestError(error, 'Failed to load overlays.')
    );
  }
});

export const fetchOverlayTemplateDetail = createAsyncThunk<
  OverlayTemplateDetailDto,
  { uuid: string; scope: OverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/fetchDetail',
  async ({ uuid, scope }, { rejectWithValue }) => {
    try {
      return await getOverlayTemplate(uuid, scope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to load overlay.')
      );
    }
  }
);

export const saveOverlayTemplate = createAsyncThunk<
  OverlayTemplateDetailDto,
  {
    uuid?: string;
    payload: OverlayTemplateWritePayload;
    scope: WritableOverlayTemplateApiScope;
  },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/save',
  async ({ uuid, payload, scope }, { rejectWithValue }) => {
    try {
      return uuid
        ? await updateOverlayTemplate(uuid, payload, scope)
        : await createOverlayTemplate(payload, scope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to save overlay.')
      );
    }
  }
);

const scopesMatch = (
  source: OverlayTemplateApiScope,
  target: WritableOverlayTemplateApiScope
) => {
  if (source.kind !== target.kind) return false;
  if (source.kind === 'account' && target.kind === 'account') return true;
  return (
    source.kind === 'profile' &&
    target.kind === 'profile' &&
    source.locationUuid === target.locationUuid
  );
};

const copyConfiguration = (
  source: OverlayTemplateDetailDto,
  title: string
): Record<string, unknown> => {
  if (Object.keys(source.configuration).length > 0) {
    return source.configuration;
  }

  return createDefaultTemplateForAspectRatio(
    `overlay-template-copy-${source.uuid}`,
    title,
    source.aspect_ratio
  ) as unknown as Record<string, unknown>;
};

export const copyOverlayTemplate = createAsyncThunk<
  OverlayTemplateDetailDto,
  {
    sourceUuid: string;
    sourceScope: OverlayTemplateApiScope;
    targetScope: WritableOverlayTemplateApiScope;
    title: string;
  },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/copy',
  async (
    { sourceUuid, sourceScope, targetScope, title },
    { rejectWithValue }
  ) => {
    try {
      const source = await getOverlayTemplate(sourceUuid, sourceScope);
      const payload: OverlayTemplateWritePayload = {
        title,
        configuration: copyConfiguration(source, title),
        aspect_ratio: source.aspect_ratio,
        preview_image_uuid: source.preview_image?.uuid ?? null,
        folder_uuid: scopesMatch(sourceScope, targetScope)
          ? source.folder_uuid ?? null
          : null
      };
      return await createOverlayTemplate(payload, targetScope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to copy overlay.')
      );
    }
  }
);

export const removeOverlayTemplate = createAsyncThunk<
  string,
  { uuid: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>('overlayTemplates/delete', async ({ uuid, scope }, { rejectWithValue }) => {
  try {
    await deleteOverlayTemplate(uuid, scope);
    return uuid;
  } catch (error: any) {
    return rejectWithValue(
      toRequestError(error, 'Failed to delete overlay.')
    );
  }
});

export const addOverlayTemplateFolder = createAsyncThunk<
  OverlayTemplateFolderDto,
  { name: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/addFolder',
  async ({ name, scope }, { rejectWithValue }) => {
    try {
      return await createOverlayTemplateFolder(name, scope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to create overlay folder.')
      );
    }
  }
);

export const moveOverlayTemplateToFolder = createAsyncThunk<
  OverlayTemplateDetailDto,
  {
    uuid: string;
    folderUuid: string | null;
    scope: WritableOverlayTemplateApiScope;
  },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/moveToFolder',
  async ({ uuid, folderUuid, scope }, { rejectWithValue }) => {
    try {
      return await updateOverlayTemplate(
        uuid,
        { folder_uuid: folderUuid },
        scope
      );
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to change the overlay folder.')
      );
    }
  }
);

export const renameOverlayTemplateFolder = createAsyncThunk<
  OverlayTemplateFolderDto,
  { uuid: string; name: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/renameFolder',
  async ({ uuid, name, scope }, { rejectWithValue }) => {
    try {
      return await updateOverlayTemplateFolder(uuid, name, scope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to rename overlay folder.')
      );
    }
  }
);

export const removeOverlayTemplateFolder = createAsyncThunk<
  string,
  { uuid: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/deleteFolder',
  async ({ uuid, scope }, { rejectWithValue }) => {
    try {
      await deleteOverlayTemplateFolder(uuid, scope);
      return uuid;
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to delete overlay folder.')
      );
    }
  }
);

export const uploadOverlayTemplatePreview = createAsyncThunk<
  string,
  {
    file: File;
    locationUuid?: string;
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  },
  OverlayTemplateThunkConfig
>('overlayTemplates/uploadPreview', async (options, { rejectWithValue }) => {
  try {
    const upload = await uploadOverlayTemplatePreviewFile(options);
    return upload.uploaded_object_data?.uuid ?? upload.uuid;
  } catch (error: any) {
    return rejectWithValue(
      toRequestError(error, 'Failed to upload overlay preview.')
    );
  }
});

const overlayTemplatesSlice = createSlice({
  name: 'overlayTemplates',
  initialState,
  reducers: {
    clearActiveTemplate: (state) => {
      state.activeTemplate = null;
      state.fieldErrors = {};
    },
    clearOverlayTemplateError: (state) => {
      state.error = null;
      state.fieldErrors = {};
    },
    setActiveTemplate: (
      state,
      action: PayloadAction<OverlayTemplateDetailDto | null>
    ) => {
      state.activeTemplate = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverlayTemplateLibrary.pending, (state, action) => {
        state.loadingLibrary = true;
        state.libraryRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(fetchOverlayTemplateLibrary.fulfilled, (state, action) => {
        if (state.libraryRequestId !== action.meta.requestId) return;
        state.loadingLibrary = false;
        state.libraryRequestId = null;
        state.loadedLibraryScopeKey = getOverlayTemplateScopeKey(
          action.meta.arg.scope
        );
        state.templates = action.payload.templates.results;
        state.folders = action.payload.folders.results;
        state.count =
          action.payload.templates.count ??
          action.payload.templates.results.length;
        state.next = action.payload.templates.next;
        state.previous = action.payload.templates.previous;
      })
      .addCase(fetchOverlayTemplateLibrary.rejected, (state, action) => {
        if (state.libraryRequestId !== action.meta.requestId) return;
        state.loadingLibrary = false;
        state.libraryRequestId = null;
        state.error =
          action.payload?.message ?? 'Failed to load overlays.';
      })
      .addCase(fetchOverlayTemplateDetail.pending, (state) => {
        state.loadingTemplate = true;
        state.error = null;
      })
      .addCase(fetchOverlayTemplateDetail.fulfilled, (state, action) => {
        state.loadingTemplate = false;
        state.activeTemplate = action.payload;
      })
      .addCase(fetchOverlayTemplateDetail.rejected, (state, action) => {
        state.loadingTemplate = false;
        state.error =
          action.payload?.message ?? 'Failed to load overlay.';
      })
      .addCase(saveOverlayTemplate.pending, (state) => {
        state.savingTemplate = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(saveOverlayTemplate.fulfilled, (state, action) => {
        state.savingTemplate = false;
        state.activeTemplate = action.payload;
        upsertTemplate(state, action.payload);
      })
      .addCase(saveOverlayTemplate.rejected, (state, action) => {
        state.savingTemplate = false;
        state.error =
          action.payload?.message ?? 'Failed to save overlay.';
        state.fieldErrors = action.payload?.fieldErrors ?? {};
      })
      .addCase(copyOverlayTemplate.pending, (state) => {
        state.savingTemplate = true;
        state.error = null;
      })
      .addCase(copyOverlayTemplate.fulfilled, (state, action) => {
        state.savingTemplate = false;
        state.activeTemplate = action.payload;
        upsertTemplate(state, action.payload);
      })
      .addCase(copyOverlayTemplate.rejected, (state, action) => {
        state.savingTemplate = false;
        state.error =
          action.payload?.message ?? 'Failed to copy overlay.';
      })
      .addCase(removeOverlayTemplate.pending, (state, action) => {
        state.deletingTemplateUuid = action.meta.arg.uuid;
        state.error = null;
      })
      .addCase(removeOverlayTemplate.fulfilled, (state, action) => {
        state.deletingTemplateUuid = null;
        state.templates = state.templates.filter(
          (template) => template.uuid !== action.payload
        );
        state.count = Math.max(0, state.count - 1);
        if (state.activeTemplate?.uuid === action.payload) {
          state.activeTemplate = null;
        }
      })
      .addCase(removeOverlayTemplate.rejected, (state, action) => {
        state.deletingTemplateUuid = null;
        state.error =
          action.payload?.message ?? 'Failed to delete overlay.';
      })
      .addCase(moveOverlayTemplateToFolder.pending, (state, action) => {
        state.movingTemplateUuid = action.meta.arg.uuid;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(moveOverlayTemplateToFolder.fulfilled, (state, action) => {
        state.movingTemplateUuid = null;
        upsertTemplate(state, action.payload);
        if (state.activeTemplate?.uuid === action.payload.uuid) {
          state.activeTemplate = action.payload;
        }
      })
      .addCase(moveOverlayTemplateToFolder.rejected, (state, action) => {
        state.movingTemplateUuid = null;
        state.error =
          action.payload?.message ?? 'Failed to change the overlay folder.';
        state.fieldErrors = action.payload?.fieldErrors ?? {};
      })
      .addCase(addOverlayTemplateFolder.fulfilled, (state, action) => {
        state.mutatingFolder = false;
        state.folders = [action.payload, ...state.folders];
      })
      .addCase(renameOverlayTemplateFolder.fulfilled, (state, action) => {
        state.mutatingFolder = false;
        const index = state.folders.findIndex(
          (folder) => folder.uuid === action.payload.uuid
        );
        if (index >= 0) state.folders[index] = action.payload;
      })
      .addCase(removeOverlayTemplateFolder.fulfilled, (state, action) => {
        state.mutatingFolder = false;
        state.folders = state.folders.filter(
          (folder) => folder.uuid !== action.payload
        );
        const templateCountBeforeDelete = state.templates.length;
        state.templates = state.templates.filter(
          (template) => template.folder_uuid !== action.payload
        );
        state.count = Math.max(
          0,
          state.count - (templateCountBeforeDelete - state.templates.length)
        );
        if (state.activeTemplate?.folder_uuid === action.payload) {
          state.activeTemplate = null;
        }
      })
      .addCase(uploadOverlayTemplatePreview.pending, (state) => {
        state.uploadingPreview = true;
        state.error = null;
      })
      .addCase(uploadOverlayTemplatePreview.fulfilled, (state) => {
        state.uploadingPreview = false;
      })
      .addCase(uploadOverlayTemplatePreview.rejected, (state, action) => {
        state.uploadingPreview = false;
        state.error =
          action.payload?.message ?? 'Failed to upload overlay preview.';
      })
      .addMatcher(
        (action) =>
          [
            addOverlayTemplateFolder.pending.type,
            renameOverlayTemplateFolder.pending.type,
            removeOverlayTemplateFolder.pending.type
          ].includes(action.type),
        (state) => {
          state.mutatingFolder = true;
          state.error = null;
          state.fieldErrors = {};
        }
      )
      .addMatcher(
        (action) =>
          [
            addOverlayTemplateFolder.rejected.type,
            renameOverlayTemplateFolder.rejected.type,
            removeOverlayTemplateFolder.rejected.type
          ].includes(action.type),
        (state, action: any) => {
          state.mutatingFolder = false;
          state.error =
            action.payload?.message ?? 'Failed to update overlay folders.';
          state.fieldErrors = action.payload?.fieldErrors ?? {};
        }
      );
  }
});

const upsertTemplate = (
  state: OverlayTemplatesState,
  template: OverlayTemplateSummaryDto
) => {
  const index = state.templates.findIndex(
    (item) => item.uuid === template.uuid
  );
  if (index >= 0) {
    state.templates[index] = template;
  } else {
    state.templates = [template, ...state.templates];
    state.count += 1;
  }
};

export const overlayTemplateActions = overlayTemplatesSlice.actions;
export default overlayTemplatesSlice.reducer;
